import { request } from "./request";

function extractListData(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}

export const getHeadlinesByCategory = async (categoryId, row) => {
  try {
    const result = await request({
      url: "/gjmsWeb/articles/queryFullByCategoryId",
      method: "get",
      data: {
        categoryId: String(categoryId),
        row: String(row),
      },
    });
    return Array.isArray(result?.data) ? result.data : [];
  } catch (error) {
    console.error("获取头条列表失败:", error);
    throw error;
  }
};

export const getArticlesByCategoryPage = async (categoryId, rows, page) => {
  try {
    const result = await request({
      url: "/gjmsWeb/articles/queryPageByCategoryId",
      method: "get",
      data: {
        categoryId: String(categoryId),
        rows: String(rows),
        page: String(page),
      },
    });
    return extractListData(result?.data);
  } catch (error) {
    console.error("获取热点新闻分页列表失败:", error);
    throw error;
  }
};

export const getRelatedArticles = async (categoryId, rows, page, currentId) => {
  try {
    const data = {
      categoryId: String(categoryId),
      rows: String(rows),
      page: String(page),
    };
    if (currentId !== undefined && currentId !== null && currentId !== "") {
      data.currentId = String(currentId);
    }
    const result = await request({
      url: "/gjmsWeb/articles/queryPageByRelation",
      method: "get",
      data,
    });
    return extractListData(result?.data);
  } catch (error) {
    console.error("获取热点新闻分页列表失败:", error);
    throw error;
  }
};

/**
 * 获取热点新闻列表（用于首页热点新闻区置顶展示）。
 * @returns {Promise<any[]>} 热点新闻数组
 */
export const getHotArticlesList = async () => {
  try {
    const result = await request({
      url: "/gjmsWeb/articles/queryHotArticlesList",
      method: "get",
    });
    return extractListData(result?.data);
  } catch (error) {
    console.error("获取热点新闻置顶列表失败:", error);
    throw error;
  }
};

/**
 * 获取新闻排行榜 Top10（今日/一周）。
 * @returns {Promise<{ todayTop10: any[]; weekTop10: any[] }>} 排行榜数据
 */
export const getClickCountHotRankTop10 = async () => {
  try {
    const result = await request({
      url: "/gjmsWeb/articles/queryClickCountHotRankTop10",
      method: "get",
    });
    const data = result?.data ?? {};
    return {
      todayTop10: Array.isArray(data?.todayTop10) ? data.todayTop10 : [],
      weekTop10: Array.isArray(data?.weekTop10) ? data.weekTop10 : [],
    };
  } catch (error) {
    console.error("获取新闻排行榜失败:", error);
    throw error;
  }
};

export const getArticlesPage = async (rows, page) => {
  try {
    const result = await request({
      url: "/gjmsWeb/articles/queryPage",
      method: "get",
      data: {
        rows: String(rows),
        page: String(page),
      },
    });
    return extractListData(result?.data);
  } catch (error) {
    console.error("获取新闻分页列表失败:", error);
    throw error;
  }
};

/**
 * 根据关键词查询稿件列表。
 * @param {string} keyword 关键词
 * @param {number|string} [rows=20] 每页条数
 * @param {number|string} [page=1] 页码
 * @returns {Promise<any[]>} 查询结果列表
 */
export const queryArticlesByKeyword = async (keyword, rows = 20, page = 1) => {
  try {
    const result = await request({
      url: "/gjmsWeb/articles/queryArticlesByKeyword",
      method: "get",
      data: {
        keyword: String(keyword ?? "").trim(),
        rows: String(rows),
        page: String(page),
      },
    });
    return extractListData(result?.data);
  } catch (error) {
    console.error("根据关键词查询稿件失败:", error);
    throw error;
  }
};

export const getArticleVoById = async (id, key) => {
  try {
    const data = { id: String(id) };
    if (key) {
      data.key = String(key);
    }
    const result = await request({
      url: "/gjmsWeb/articles/queryArticleVo",
      method: "get",
      data,
    });
    return result?.data ?? {};
  } catch (error) {
    console.error("获取新闻详情失败:", error);
    throw error;
  }
};

/**
 * 根据稿件 ID 按关键词查询已发布推荐稿件列表。
 * @param {number|string} id 稿件 ID
 * @returns {Promise<any[]>} 推荐稿件列表
 */
export const queryPublishedWithKeyword = async (id) => {
  try {
    const result = await request({
      url: "/gjmsWeb/articles/queryPublishedWithKeyword",
      method: "get",
      data: {
        id: String(id),
      },
    });
    return extractListData(result?.data);
  } catch (error) {
    console.error("查询已发布稿件及关键词失败:", error);
    throw error;
  }
};

/**
 * 获取直播稿件详情。
 * @param {number|string} id 稿件 ID
 * @param {string} [key] 可选的访问密钥
 * @returns {Promise<any>} 直播稿件详情对象
 */
export const getLiveArticleVo = async (id, key) => {
  try {
    const data = {};
    if (id) {
      data.id = String(id);
    }
    if (key) {
      data.key = String(key);
    }
    const result = await request({
      url: "/gjmsWeb/articles/queryLiveArticleVo",
      method: "get",
      data,
    });
    return result?.data ?? {};
  } catch (error) {
    console.error("获取直播详情失败:", error);
    throw error;
  }
};

/**
 * 根据文章 ID 获取专题分类及其稿件列表。
 * @param {number|string} articleId 文章 ID
 * @returns {Promise<any>} 专题数据对象
 */
export const getSpecialCategoriesByArticleId = async (articleId, key) => {
  try {
    const data = { articleId: String(articleId) };
    if (key) {
      data.key = String(key);
    }
    const result = await request({
      url: "/gjmsWeb/articles/querySpecialCategoriesByArticleId",
      method: "get",
      data,
    });
    return result?.data ?? {};
  } catch (error) {
    console.error("获取专题分类数据失败:", error);
    throw error;
  }
};

/**
 * 记录内容阅读请求日志。
 * @param {number|string} contentId 内容 ID
 * @returns {Promise<boolean>} 是否请求成功
 */
export const addContentReadRequestLog = async (contentId) => {
  try {
    await request({
      url: "/gjmsWeb/articles/addContentReadRequestLog",
      method: "post",
      data: {
        contentId: Number(contentId),
      },
    });
    return true;
  } catch (error) {
    console.error("记录内容阅读请求日志失败:", error);
    return false;
  }
};

export const queryArticleVoByTempKey = async (key) => {
  try {
    const result = await request({
      url: "/gjmsWeb/articles/queryArticleVoByTempKey",
      method: "get",
      data: {
        key: String(key),
      },
    });
    if (result?.id) return result;
    return result?.data ?? {};
  } catch (error) {
    console.error("获取临时文章详情失败:", error);
    throw error;
  }
};

/**
 * 内容黑词检测（预览态 from=wechat 敏感词高亮）。
 * 对齐 PC：POST /camel-gateway/gjmsAdmin/blackWord/checkContent
 * @param {string} content 待检测纯文本
 * @param {string} [platform=gjms] 平台标识
 * @returns {Promise<unknown>}
 */
export const checkContent = async (content, platform = "gjms") => {
  try {
    const res = await fetch("/camel-gateway/gjmsAdmin/blackWord/checkContent", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      body: JSON.stringify({
        platform: String(platform ?? "gjms"),
        content: String(content ?? ""),
      }),
    });
    if (!res.ok) {
      throw new Error("HTTP " + res.status);
    }
    const result = await res.json();
    return result?.data ?? result;
  } catch (error) {
    console.error("内容黑词检查失败:", error);
    throw error;
  }
};
